import { useCallback, useEffect, useState } from "react";

// Lucide Icons
import { Construction } from "lucide-react";
import { toast } from "sonner";

import LazySearchSelect from "../../components/shared/LazySearchSelect.jsx";
import PageHeader from "../../components/shared/PageHeader";
import Pagination from "../../components/shared/Pagination";
import SearchInput from "../../components/shared/SearchInput";
import WordCount from "../../components/shared/WordCount.jsx";
// Shadcn UI Components
import { Button } from "../../components/ui/button.jsx";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog.jsx";
import { Input } from "../../components/ui/input.jsx";
import { Label } from "../../components/ui/label.jsx";
import { Switch } from "../../components/ui/switch.jsx";
import { Textarea } from "../../components/ui/textarea.jsx";
// Auth Hook - Import from App.js context
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { cn, getCharCount } from "../../lib/utils.js";

import DuplicateProjectDialog from "./component/DuplicateProjectDialog";
import EditProjectDialog from "./component/EditProjectDialog";
import ProjectTable from "./component/ProjectTable";
import { useProjectApi } from "./hooks/useProjectApi";

// Projects Component
const Projects = () => {
  const { users, refreshProjects } = useData();
  const { user } = useAuth();
  const {
    projects,
    pagination,
    searchTerm,
    setSearchTerm,
    createProject: apiCreateProject,
    deleteProject,
    updateProject,
    duplicateProject: apiDuplicateProject,
    createDefaultMilestones,
    statusFilter,
    setStatusFilter,
    sortBy,
    setSortBy,
    direction,
    setDirection,
    showDeleted,
    setShowDeleted
  } = useProjectApi(10);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    location: "",
    startDate: "",
    projectManagerId: "",
    estimatedBudget: "",
    defaultMilestones: true
  });
  const [editingProject, setEditingProject] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDuplicateOpen, setIsDuplicateOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState("");
  const [projectToDuplicate, setProjectToDuplicate] = useState(null);
  const [includeTasks, setIncludeTasks] = useState(true);

  const isDescriptionOverLimit = getCharCount(newProject.description) > 500;

  // Default the Project Manager to the current user when opening create dialog
  useEffect(() => {
    if (user?.id && !newProject.projectManagerId && isCreateOpen) {
      setNewProject((prev) => ({ ...prev, projectManagerId: user.id }));
    }
  }, [user, isCreateOpen, newProject.projectManagerId]);

  const onEdit = (project) => {
    setEditingProject({
      ...project,
      startDate: project.startDate ? new Date(project.startDate).toISOString().split("T")[0] : ""
    });
    setIsEditOpen(true);
  };

  const onDuplicate = (project) => {
    setProjectToDuplicate(project);
    setDuplicateName(`${project.name} (Copy)`);
    setIsDuplicateOpen(true);
  };

  const createProject = async (e) => {
    e.preventDefault();
    if (isDescriptionOverLimit) {
      toast.error("Description exceeds the 500-character limit.");
      return;
    }
    try {
      await apiCreateProject({
        ...newProject,
        startDate: new Date(newProject.startDate).toISOString(),
        estimatedBudget: newProject.estimatedBudget ? parseFloat(newProject.estimatedBudget) : null
      });

      setNewProject({
        name: "",
        description: "",
        location: "",
        startDate: "",
        projectManagerId: "",
        estimatedBudget: "",
        defaultMilestones: true
      });
      setIsCreateOpen(false);
    } catch (error) {
      // toast is handled in hook
    }
  };

  const handleUpdate = async (updatedProject) => {
    try {
      const data = {
        ...updatedProject,
        startDate: updatedProject.startDate ? new Date(updatedProject.startDate).toISOString() : null,
        estimatedBudget: updatedProject.estimatedBudget ? parseFloat(updatedProject.estimatedBudget) : null
      };

      await updateProject(updatedProject.id, data);
      setIsEditOpen(false);
      setEditingProject(null);
    } catch (error) {
      // toast is handled in hook
    }
  };

  const handleDuplicate = async (duplicateData) => {
    try {
      await apiDuplicateProject(projectToDuplicate.id, duplicateData);
      setIsDuplicateOpen(false);
      setProjectToDuplicate(null);
    } catch (error) {
      // toast is handled in hook
    }
  };

  const { setCurrentPage } = pagination;

  const handleSearch = useCallback(
    (term) => {
      setCurrentPage(0);
      setSearchTerm(term);
    },
    [setCurrentPage, setSearchTerm]
  );

  return (
    <div className="flex-1 flex flex-col animate-in fade-in duration-500">
      <div className="flex-1 space-y-6">
        <PageHeader
          title="Projects"
          subtitle="Manage your construction projects"
          actionLabel={user?.permissions?.project?.create ? "New Project" : null}
          onActionClick={() => setIsCreateOpen(true)}
          pageIcon={Construction}
          extraActions={
            <div className="flex items-center gap-4">
              <SearchInput placeholder="Search projects..." className="md:w-72" onSearch={handleSearch} />
            </div>
          }
        />

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Project</DialogTitle>
              <DialogDescription>Add a new construction project to track</DialogDescription>
            </DialogHeader>
            <form onSubmit={createProject} className="space-y-4">
              <div>
                <Label htmlFor="name">Project Name</Label>
                <Input
                  id="name"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={newProject.location}
                  onChange={(e) => setNewProject({ ...newProject, location: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={newProject.startDate}
                  onChange={(e) => setNewProject({ ...newProject, startDate: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="projectManagerId">Project Manager</Label>
                <LazySearchSelect
                  value={newProject.projectManagerId}
                  onChange={(value) => setNewProject({ ...newProject, projectManagerId: value })}
                  placeholder="Select project manager"
                  searchPlaceholder="Search managers..."
                  apiUrl="/users"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newProject.description}
                  onChange={(e) =>
                    setNewProject({
                      ...newProject,
                      description: e.target.value
                    })
                  }
                  className={cn(isDescriptionOverLimit && "border-red-500")}
                  rows={3}
                />
                <WordCount text={newProject.description} />
              </div>
              <div>
                <Label htmlFor="estimatedBudget">Estimated Budget (₹)</Label>
                <Input
                  id="estimatedBudget"
                  type="number"
                  step="0.01"
                  value={newProject.estimatedBudget}
                  onChange={(e) =>
                    setNewProject({
                      ...newProject,
                      estimatedBudget: e.target.value
                    })
                  }
                  placeholder="Enter project budget"
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="defaultMilestones"
                  checked={Boolean(newProject.defaultMilestones)}
                  onCheckedChange={(checked) =>
                    setNewProject((prev) => ({
                      ...prev,
                      defaultMilestones: Boolean(checked)
                    }))
                  }
                />
                <Label htmlFor="defaultMilestones" className="cursor-pointer">
                  Default Milestones
                </Label>
              </div>
              <Button type="submit" variant="primary" className="w-full py-6 rounded-lg" disabled={isDescriptionOverLimit}>
                Create Project
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <EditProjectDialog
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          project={editingProject}
          onUpdate={handleUpdate}
          users={users}
        />

        <DuplicateProjectDialog
          open={isDuplicateOpen}
          onOpenChange={setIsDuplicateOpen}
          project={projectToDuplicate}
          onDuplicate={handleDuplicate}
        />

        <ProjectTable
          projects={projects}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          deleteProject={deleteProject}
          createDefaultMilestones={createDefaultMilestones}
          filters={{
            status: statusFilter
          }}
          onFilterChange={(filterName, value) => {
            if (filterName === "status") {
              setStatusFilter(value);
              setCurrentPage(0);
            }
          }}
          sortBy={sortBy}
          direction={direction}
          onSort={(key, dir) => {
            setSortBy(key);
            setDirection(dir);
            setCurrentPage(0);
          }}
          showDeleted={showDeleted}
          setShowDeleted={setShowDeleted}
          setCurrentPage={setCurrentPage}
        />
      </div>

      <div className="mt-8">
        <Pagination pagination={pagination} />
      </div>
    </div>
  );
};

export default Projects;
